import React, { useState } from "react";
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, CircularProgress, Chip, TextField, IconButton, InputAdornment, Grid, Avatar, Tooltip,
} from "@mui/material";
import { useDeleteCompanyMutation, useGetCompanyQuery } from "../../../Api/attoDeskApi";
import { ICompanyDetails } from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BusinessIcon from "@mui/icons-material/Business";
import StarIcon from "@mui/icons-material/Star";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";

const LARAVEL_API_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();
const thSx = { fontWeight: 600, fontSize: 10, color: "#94a3b8", textTransform: "uppercase" as const, letterSpacing: "0.08em", py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fafbfc" };

const CompanyList: React.FC = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [deleteTarget, setDeleteTarget] = useState<ICompanyDetails | null>(null);

  const { data, isLoading } = useGetCompanyQuery({ searchText: debouncedSearch, id: "" });
  const [deleteCompany] = useDeleteCompanyMutation();

  const companies: ICompanyDetails[] = Array.isArray(data?.data) ? (data?.data as ICompanyDetails[]) : [];

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await deleteCompany(deleteTarget.id.toString()).unwrap();
      if (res.status) { showMessage("Company deleted"); setDeleteTarget(null); }
      else showErrorMessage(res.message);
    } catch { showErrorMessage("Failed to delete"); }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 3 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 3, background: "linear-gradient(135deg, #1e293b, #334155)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(30,41,59,0.25)" }}>
          <BusinessIcon sx={{ fontSize: 24, color: "#fff" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={24} fontWeight={800} color="#0f172a" letterSpacing="-0.03em">Companies</Typography>
          <Typography fontSize={13} color="#94a3b8">Manage your company locations and details</Typography>
        </Box>
        <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => navigate("/company/create")}
          sx={{ borderRadius: 2.5, py: 1, px: 3, fontWeight: 700, fontSize: 13, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, textTransform: "none" }}>
          Add Company
        </Button>
      </Box>

      {/* Search */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
        <TextField size="small" placeholder="Search companies..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
          sx={{ minWidth: 300, "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#fafbfc" } }} />
        <Chip label={`${companies.length} companies`} size="small" sx={{ ml: 2, height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} />
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress size={28} sx={{ color: "#00838f" }} /></Box>
        ) : companies.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <BusinessIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1.5 }} />
            <Typography fontSize={16} fontWeight={700} color="#64748b" mb={0.5}>No companies yet</Typography>
            <Typography fontSize={13} color="#94a3b8" mb={3}>Add your first company to get started</Typography>
            <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => navigate("/company/create")}
              sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, textTransform: "none" }}>
              Add Company
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 50 }}>#</TableCell>
                  <TableCell sx={thSx}>Company</TableCell>
                  <TableCell sx={thSx}>Email</TableCell>
                  <TableCell sx={thSx}>Phone</TableCell>
                  <TableCell sx={thSx}>City</TableCell>
                  <TableCell sx={thSx} align="center">Default</TableCell>
                  <TableCell sx={thSx} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((row, idx) => (
                  <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#fafbfe" } }}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontSize={12} color="#94a3b8" fontWeight={600}>{idx + 1}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar src={row.CompanyLogo ? `${LARAVEL_API_URL}/${row.CompanyLogo}` : undefined}
                          sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#f1f5f9", fontSize: 14, fontWeight: 700, color: "#64748b" }}>
                          {row.companyName?.charAt(0) || "C"}
                        </Avatar>
                        <Box>
                          <Typography fontSize={13} fontWeight={600} color="#1e293b">{row.companyName}</Typography>
                          {row.companySlog && <Typography fontSize={11} color="#94a3b8">{row.companySlog}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontSize={12} color="#64748b">{row.companyEmail || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontSize={12} color="#64748b">{row.companyMobileNumber || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontSize={12} color="#64748b">{row.companyCity || "—"}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      {row.isDefault === 1 ? (
                        <StarIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                      ) : (
                        <Typography fontSize={11} color="#cbd5e1">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        <Tooltip title="Open">
                          <IconButton size="small" onClick={() => navigate(`/company/${row.id}`)}
                            sx={{ width: 30, height: 30, borderRadius: 1.5, border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f0fdf4", borderColor: "#86efac" } }}>
                            <EditIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteTarget(row)}
                            sx={{ width: 30, height: 30, borderRadius: 1.5, border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#fef2f2", borderColor: "#fca5a5" } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14, color: "#ef4444" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Popup */}
      {deleteTarget && (
        <DeletePopup open={!!deleteTarget} handleCloseDelete={() => setDeleteTarget(null)} onConfirm={handleDelete}
          title="Delete Company" content={`Are you sure you want to delete "${deleteTarget.companyName}"?`} />
      )}
    </Box>
  );
};

export default CompanyList;
